import React, { useState } from 'react';
import {
  Sparkles,
  Star,
  Phone,
  CheckCircle2,
  ShieldCheck,
  Plus,
  Search,
  ExternalLink,
} from 'lucide-react';
import { ConciergeService } from '../../types';
import { INITIAL_CONCIERGE_SERVICES } from '../../data/adminModulesData';

export const AdminServicesView: React.FC = () => {
  const [services, setServices] = useState<ConciergeService[]>(INITIAL_CONCIERGE_SERVICES);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  // New service form
  const [newName, setNewName] = useState('');
  const [newProvider, setNewProvider] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newPrice, setNewPrice] = useState(500);
  const [newUnitDesc, setNewUnitDesc] = useState('บาท / ครั้ง');
  const [newDesc, setNewDesc] = useState('');

  const filteredServices = services.filter(
    (s) =>
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.provider_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddService = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName || !newProvider) return;

    const newSrv: ConciergeService = {
      id: `srv-${Date.now()}`,
      name: newName,
      category: 'handyman',
      provider_name: newProvider,
      rating: 5.0,
      phone: newPhone || '035-998-112',
      starting_price: Number(newPrice),
      unit_desc: newUnitDesc,
      description: newDesc,
      badges: ['พาร์ทเนอร์นิติบุคคล', 'ผ่านการตรวจประวัติ'],
      is_verified: true,
    };

    setServices([newSrv, ...services]);
    setShowAddModal(false);
    setNewName('');
    setNewProvider('');
    setNewDesc('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-pink-50 text-pink-600 border border-pink-200">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900">
              13. บริการเสริมและศูนย์พาร์ทเนอร์ช่างวิชาชีพ (On-demand Concierge & Extra Services)
            </h2>
            <p className="text-xs text-slate-500">
              รวมบริการช่างล้างแอร์, แม่บ้าน, ตัดแต่งสวน, กำจัดปลวก ที่ผ่านการคัดกรองประวัติเพื่อลูกบ้าน
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
        >
          <Plus className="w-4 h-4" /> เพิ่มผู้ให้บริการ / พาร์ทเนอร์
        </button>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredServices.map((service) => (
          <div
            key={service.id}
            className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs flex flex-col justify-between space-y-4 hover:border-slate-300 transition-all"
          >
            <div className="space-y-2">
              <div className="flex items-start justify-between">
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-pink-50 text-pink-700 border border-pink-200 flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3 text-pink-600" /> ตรวจสอบประวัติแล้ว
                </span>

                <div className="flex items-center gap-1 text-amber-500 text-xs font-bold font-mono">
                  <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                  <span>{service.rating}</span>
                </div>
              </div>

              <h3 className="font-bold text-slate-900 text-sm leading-snug">{service.name}</h3>
              <p className="text-xs text-slate-500 font-medium">โดย: {service.provider_name}</p>
              
              <p className="text-xs text-slate-600 leading-relaxed bg-slate-50 p-3 rounded-xl">
                {service.description}
              </p>

              {/* Badges */}
              <div className="flex flex-wrap gap-1 pt-1">
                {service.badges.map((badge, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 text-slate-700"
                  >
                    ✓ {badge}
                  </span>
                ))}
              </div>
            </div>

            <div className="pt-3 border-t border-slate-100 space-y-2">
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-slate-500">ราคาเริ่มต้น:</span>
                <div>
                  <span className="text-lg font-bold text-blue-700 font-mono">
                    ฿{service.starting_price.toLocaleString()}
                  </span>
                  <span className="text-[11px] text-slate-500 ml-1 font-sans">{service.unit_desc}</span>
                </div>
              </div>

              <a
                href={`tel:${service.phone}`}
                className="w-full py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-colors"
              >
                <Phone className="w-3.5 h-3.5 text-emerald-400" />
                <span>โทรติดต่อ {service.phone}</span>
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* Add Service Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden">
            <div className="p-5 bg-slate-900 text-white flex items-center justify-between">
              <h3 className="font-bold text-base">เพิ่มบริการเสริม / พันธมิตรช่าง</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">✕</button>
            </div>

            <form onSubmit={handleAddService} className="p-6 space-y-4 text-xs">
              <div>
                <label className="font-semibold text-slate-700 block mb-1">ชื่อบริการ *</label>
                <input
                  type="text"
                  required
                  placeholder="เช่น บริการล้างถังพักน้ำและระบบประปา"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ชื่อบริษัท / ผู้ให้บริการ *</label>
                  <input
                    type="text"
                    required
                    placeholder="เช่น อยุธยา วอเตอร์ แคร์"
                    value={newProvider}
                    onChange={(e) => setNewProvider(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">เบอร์โทรศัพท์ติดต่อ *</label>
                  <input
                    type="text"
                    required
                    placeholder="081-xxx-xxxx"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ราคาเริ่มต้น (บาท)</label>
                  <input
                    type="number"
                    value={newPrice}
                    onChange={(e) => setNewPrice(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">หน่วยราคา</label>
                  <input
                    type="text"
                    value={newUnitDesc}
                    onChange={(e) => setNewUnitDesc(e.target.value)}
                    placeholder="เช่น บาท / ถัง"
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">รายละเอียดบริการ</label>
                <textarea
                  rows={3}
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  placeholder="ระบุขอบเขตงาน..."
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-blue-700 hover:bg-blue-800 text-white font-bold"
                >
                  บันทึกบริการ
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
